import { History } from 'history';
import qs from 'qs';
import { PartialRecord } from './types';

export function getBaseURL() {
  const { protocol, hostname, port } = window.location;

  return `${protocol}//${hostname}${port ? ':' + port : ''}`;
}

function buildQueryString(params: any, addPrefix = false) {
  const qs = Object.keys(params || {})
    .filter((key) => typeof params[key] !== 'undefined')
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
    .join('&');

  if (!addPrefix || !qs.length) {
    return qs;
  }

  return `?${qs}`;
}

export function getQueryParam(
  param: string,
  defaultValue: any = null,
  parser?: (input: string) => any,
): any {
  // Query parameters are unavailable while rendering on the server.
  if (typeof window === 'undefined') {
    return defaultValue;
  }

  const qs = parseQueryString(window.location.search);

  return qs[param] ? (parser ? parser(qs[param]) : qs[param]) : defaultValue;
}

export function parseQueryString(
  query: string,
  cast = false,
  parsers: PartialRecord<string, (input: string) => any> = {},
): Record<string, any> {
  const parsedQuery = qs.parse(query.replace(/^\?/, ''));

  let beforeParsers: Record<string, any>;

  if (!cast) {
    beforeParsers = parsedQuery;
  } else {
    beforeParsers = Object.keys(parsedQuery).reduce((all: any, key) => {
      let value = parsedQuery[key] as any;

      if (value === 'true') {
        value = true;
      } else if (value === 'false') {
        value = false;
      } else if (/^\d+$/.test(value)) {
        value = Number(value);
      }

      all[key] = value;

      return all;
    }, {});
  }

  return Object.keys(beforeParsers).reduce((all: any, key) => {
    if (beforeParsers[key] === '') {
      return all;
    }

    if (parsers[key]) {
      all[key] = parsers[key]!(beforeParsers[key]);
    } else {
      all[key] = beforeParsers[key];
    }

    return all;
  }, {});
}

export function appendQueryParam(url: any, param: any, value: any) {
  const hasQueryString = url.match(/\?/);

  return (
    url + (hasQueryString ? `&` : '?') + `${encodeURIComponent(param)}=${encodeURIComponent(value)}`
  );
}

export function cleanQuery<T extends object>(query: T): T {
  return (Object.keys(query) as (keyof typeof query)[]).reduce((all, key) => {
    if (
      (typeof key === 'string' && key.startsWith('_')) ||
      (query[key] as any) === '' ||
      query[key] === null ||
      typeof query[key] === 'undefined'
    ) {
      return all;
    }

    return {
      ...all,
      [key]: query[key],
    };
  }, {}) as T;
}

export function downloadFile(url: string, newTab = true) {
  if (newTab) {
    window.open(url, '_blank');
  } else {
    window.location.href = url;
  }
}

export function updateQueryParam(
  history: History,
  param: string,
  value: string | number,
  doPush = true,
) {
  const qs = parseQueryString(window.location.search);

  const updatedParams = {
    ...qs,
    [param]: value,
  };

  if (typeof value === 'undefined' || value === '') {
    delete updatedParams[param];
  }

  const params = {
    pathname: window.location.pathname,
    search: buildQueryString(updatedParams),
  };

  if (doPush) {
    history.push(params);
  } else {
    history.replace(params);
  }
}

export function updateQuery(history: History, query: any) {
  const current = parseQueryString(window.location.search);

  const updatedParams = cleanQuery({
    ...current,
    ...query,
  });

  history.push({
    pathname: window.location.pathname,
    search: buildQueryString(updatedParams),
  });
}
